import classNames from "classnames";
import React, { HTMLAttributes } from "react";

import { noop } from "@utils/helpers";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { Fade, FadeProps } from "../Fade/fade.component";

export type AlertProps = HTMLAttributes<HTMLDivElement> & {
    children: React.ReactNode;
    className?: string;
    color?: string;
    toggle?: () => void;
    closeClassName?: string;
    isOpen?: boolean;
    fade?: boolean;
    closeAriaLabel?: string;
    innerRef?: React.Ref<HTMLElement>;
    transition?: FadeProps<"div">;
};

const Alert = ({
    children,
    className,
    color = "success",
    toggle,
    closeClassName,
    isOpen = true,
    fade = true,
    closeAriaLabel,
    innerRef,
    transition = {
        baseClass: "fade",
        timeout: 150,
        addEndListener: noop,
        unmountOnExit: true,
    },
    ...attributes
}: AlertProps) => {
    const { t } = useGranite();

    const classes = classNames(className, "alert", `alert-${color}`, {
        "alert-dismissible": toggle,
    });

    const closeClasses = classNames("close", closeClassName);

    const alertTransition = {
        ...transition,
        baseClass: fade ? transition.baseClass : "",
        timeout: fade ? transition.timeout : 0,
    };

    return (
        <Fade
            {...attributes}
            {...alertTransition}
            addEndListener={transition.addEndListener || noop}
            tag={"div"}
            className={classes}
            in={isOpen}
            role="alert"
            ref={innerRef}
        >
            {toggle ? (
                <button
                    type="button"
                    className={closeClasses}
                    aria-label={closeAriaLabel || t("general.alert.close")}
                    onClick={toggle}
                >
                    <span aria-hidden="true">&times;</span>
                </button>
            ) : null}
            {children}
        </Fade>
    );
};

export { Alert };
